import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { permissionAdminService } from '@/services';
import type { ServiceContext } from '@prolarus/shared';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface MissingPermission {
  code: string;
  category: string;
  displayName: string;
  description: string;
}

interface SyncPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missingPermissions: MissingPermission[];
  context: ServiceContext;
  onSynced: () => void;
}

export default function SyncPermissionsDialog({
  open,
  onOpenChange,
  missingPermissions,
  context,
  onSynced,
}: SyncPermissionsDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Reset selection when dialog opens or missing permissions change
  useEffect(() => {
    if (open) {
      setSelected(new Set(missingPermissions.map(p => p.code)));
    }
  }, [open, missingPermissions]);

  const toggleAll = () => {
    if (selected.size === missingPermissions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(missingPermissions.map(p => p.code)));
    }
  };

  const toggle = (code: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const items = missingPermissions
      .filter(p => selected.has(p.code))
      .map(p => ({
        code: p.code,
        name: p.displayName,
        description: p.description,
        category: p.category,
      }));

    if (items.length === 0) return;

    setIsSaving(true);
    try {
      const result = await permissionAdminService.bulkCreateSystem(items, context);
      if (result.error) {
        toast.error(result.error.message);
        return;
      }
      toast.success(`Added ${items.length} permission${items.length > 1 ? 's' : ''}`);
      onSynced();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to add permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const allSelected = selected.size === missingPermissions.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Missing Permissions</DialogTitle>
          <DialogDescription>
            Select the permissions to add to your database. These will be created as system permissions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between py-2 border-b">
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={toggleAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <span className="text-sm text-muted-foreground">
            {selected.size} of {missingPermissions.length} selected
          </span>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {missingPermissions.map(p => (
            <div key={p.code} className="flex items-start space-x-3">
              <Checkbox
                id={p.code}
                checked={selected.has(p.code)}
                onCheckedChange={() => toggle(p.code)}
                className="mt-0.5"
              />
              <label htmlFor={p.code} className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{p.displayName}</span>
                  <Badge variant="outline" className="text-xs">{p.category}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.code}</div>
                <div className="text-xs text-muted-foreground">{p.description}</div>
              </label>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || selected.size === 0}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Selected Permissions ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
